<template>
  <div class="mb-10" data-testid="reservation-step1-form">
    <p class="fixed left-0 right-0 top-[1rem] z-[2] w-full border-b border-solid border-beige3 bg-white p-4 text-[16px] md:top-0 md:text-[17px]">Request for booking</p>
    <div class="bg-white pt-9 e2:m-auto e2:max-w-6xl">
      <!-- 体験タイトルと画像表示セクション -->
      <TitleWithImage
        class="border-b px-4 py-4 md:px-8"
        mode="SMALL"
        text-style="text-[16px] md:text-[17px] font-light leading-[22px] tracking-wider"
        :img="props.experience.images[0]?.imageUrl"
        :on-click-image="() => {}"
        :slug="props.experience.slug"
        :title="props.experience.name"
      />
      <CalendarPicker
        :calendar-events="experienceEventDict"
        :calendar-note="props.experience.calendarNote"
        :calendar-refresh-key="props.calendarRefreshKey"
        :display-month="displayMonth"
        :get-error-message="getErrorMessage"
        :is-loading="isLoading"
        :is-selected-date="isSelectedDate"
        :is-waitlist-enabled="props.isWaitlistEnabled"
        :on-change-month="onChangeMonth"
        :on-click-date="onClickDate"
        :on-click-get-notified="props.onClickGetNotified"
        :on-click-next-button="onClickNextButton"
        :on-click-previous-button="onClickPreviousButton"
        :reservation-form-step1="reservationStep1Form"
      />
      <DateTimePicker
        :can-reserve-event-options="canReserveEventOptions"
        :candidate-events="candidateEvents"
        :get-error-message="getErrorMessage"
        :initial-update-from-experience-event="initialUpdateFromExperienceEvent"
        :is-loading="isLoading"
        :is-overnight-stay-event="isOvernightStayEvent"
        :is-selected-date="isSelectedDate"
        :on-change-experience-event-id="onChangeExperienceEventId"
        :reservation-form-step1="reservationStep1Form"
      />
      <NumberOfParticipantPicker
        :default-participants="defaultParticipants"
        :experience="props.experience"
        :experience-event="props.experienceEvent"
        :get-error-message="getErrorMessage"
        :is-selected-date="isSelectedDate"
        :participants-options="participantsOptions"
        :reservation-step1-form="reservationStep1Form"
      />
      <div v-if="shouldShowPrice" class="px-4 pb-10 pt-8 md:px-8">
        <TotalPriceSection
          v-if="!props.experienceEvent.timeSlotId && !runtimeConfig.public.IS_NEW_PRICE_PLAN_ENABLED"
          :default-experience="props.experience"
          :number-of-participants="props.reservationStep1Form.numOfParticipants"
          :pre-reservation-price="props.preReservationPrice"
          :selected-experience-event="props.experienceEvent"
        />
        <TotalPriceSectionWithGuestType
          v-else-if="runtimeConfig.public.IS_NEW_PRICE_PLAN_ENABLED && props.experienceEvent.timeSlotId"
          :default-experience="props.experience"
          :experience-event="props.experienceEvent"
          :pre-reservation-price-with-guest-type="props.preReservationPriceWithGuestType"
          :selected-experience-event="props.experienceEvent"
        />
      </div>
    </div>
    <div class="fixed bottom-0 left-0 right-0 z-[2] w-full bg-[#EAEAEA]/30 p-1 backdrop-blur-[4px]">
      <div class="mx-auto flex max-w-6xl justify-center">
        <AtomsPrimaryButton class="w-full max-w-[370px] gap-y-2" data-testid="submit" gtm-id="addPaymentCard" :disabled="isSubmitDisabled" :on-click="onSuccess">
          {{ !isSending ? 'Continue' : 'Processing...' }}
        </AtomsPrimaryButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { type UseRuntimeConfig, useRuntimeConfigKey } from '@/injectionKeys';
import type { WabunkaExperienceViewModel } from '@/models/wabunka/wabunkaExperience';
import { TitleWithImage } from '@/wabunka-src/common/components';
import TotalPriceSectionWithGuestType from '@/wabunka-src/common/features/Experience/components/TotalPriceSection/TotalPriceSectionWithGuestType.vue';
import { isOvernightStay, type ExperiencePreReservationPriceViewModel, type WabunkaReservationFormStep1, type WabunkaCode } from '@/wabunka-src/features/Experience';
import { useDisplayMonth } from '@/wabunka-src/features/Experience/composables/query/useDisplayMonth';
import { type WabunkaExperienceEventViewModel } from '@/wabunka-src/features/Experience/models/experienceEvent';
import CalendarPicker from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/stateful/ReservationStep1Form/CalendarPicker/CalendarPicker.vue';
import DateTimePicker from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/stateful/ReservationStep1Form/DateTimePicker/DateTimePicker.vue';
import NumberOfParticipantPicker from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/stateful/ReservationStep1Form/NumberOfParticipantPicker/NumberOfParticipantPicker.vue';
import TotalPriceSection from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/stateful/ReservationStep1Form/TotalPriceSection/TotalPriceSection.vue';
import { useDateSelection } from '@/wabunka-src/features/Experience/pages/ExperienceDetail/composables/form/useDateSelection';
import { useExperienceEventSelection } from '@/wabunka-src/features/Experience/pages/ExperienceDetail/composables/form/useExperienceEventSelection';
import { useParticipants } from '@/wabunka-src/features/Experience/pages/ExperienceDetail/composables/form/useParticipants';
import type { ExperiencePreReservationPriceWithGuestTypeViewModel } from '@/wabunka-src/features/Experience/subFeatures/Reservation/models/wabunkaExperiencePreReservationPrice';

import type dayjs from 'dayjs';

interface Props {
  /**
   * 他箇所での変更分をカレンダーに反映するための再描画のキー
   */
  calendarRefreshKey: number;
  onSuccess: () => void;
  experienceId: string;
  experience: WabunkaExperienceViewModel;
  isSkipDateSelect?: boolean;
  isSending?: boolean;
  getErrorMessage: (s: string) => string;
  initialUpdateFromExperienceEvent: (experienceEvent: WabunkaExperienceEventViewModel) => void;
  onUpdateSelectedDateAndExpEventId: (selectedDate: dayjs.Dayjs | null, experienceEventId: string) => void;
  reservationStep1Form: WabunkaReservationFormStep1;
  experienceEvent: WabunkaExperienceEventViewModel;
  preReservationPrice?: ExperiencePreReservationPriceViewModel;
  preReservationPriceWithGuestType?: ExperiencePreReservationPriceWithGuestTypeViewModel;
  onChangeExperienceEventId: (experienceEventId: string) => void;
  onClickGetNotified: () => void;
  /**
   * ウェイトリスト登録の露出制御。フラグ OFF の間は CTA を描画しない
   */
  isWaitlistEnabled: boolean;
  appliedCode?: WabunkaCode;
}

const props = defineProps<Props>();
const injectedRuntimeConfig = inject(useRuntimeConfigKey) as UseRuntimeConfig;
const runtimeConfig = injectedRuntimeConfig();
const experienceId = computed<string>(() => props.experienceId);

const isOvernightStayEvent = computed(() => isOvernightStay(props.experience));
// 日付が選択されているかどうか？(選択されている場合は時間選択モーダル、選択されていない場合は日付選択モーダルを表示)
const isSelectedDate = computed(() => props.reservationStep1Form.selectedDate !== null);

const { experience, experienceEvent, reservationStep1Form, onUpdateSelectedDateAndExpEventId, onChangeExperienceEventId, initialUpdateFromExperienceEvent } = props;

const shouldShowPrice = computed(() => props.experience.isPublicPrice);

// ゲストの合計が最小人数に達していない場合は予約不可にするためのcomputed property
const isSubmitDisabled = computed(() => {
  // 体験イベントIDが選択されていない場合は無効
  if (props.reservationStep1Form.experienceEventId === '') {
    return true;
  }

  // 新しい価格プランが有効で、timeSlotIdがある場合のみ最少予約人数をチェック
  if (runtimeConfig.public.IS_NEW_PRICE_PLAN_ENABLED && props.experienceEvent.timeSlotId) {
    // ゲスト数とイベント料金が存在する場合
    if (props.reservationStep1Form.guestCounts && props.experienceEvent.experienceEventFees) {
      // 人数制限に含まれるゲストタイプの合計人数を計算
      let totalIncludedGuests = 0;

      for (const [guestTypeId, count] of Object.entries(props.reservationStep1Form.guestCounts)) {
        const experienceEventFee = props.experienceEvent.experienceEventFees.find((fee) => fee.guestType.id === guestTypeId);

        if (experienceEventFee && experienceEventFee.guestType.isIncludedInHeadcountLimit) {
          totalIncludedGuests += count;
        }
      }

      // 最小人数に達していない場合は無効
      if (totalIncludedGuests < props.experienceEvent.minGuestsPerReservation) {
        return true;
      }
    }
  }

  return false;
});

// 参加人数の選択肢
const { defaultParticipants, participantsOptions } = useParticipants({
  experience,
  experienceEvent,
  reservationStep1Form,
});

// 日付選択
const { startDateFrom, startDateTo, onChangeMonth, onClickDate, onClickNextButton, onClickPreviousButton } = useDateSelection({
  reservationStep1Form,
  onUpdateSelectedDateAndExpEventId,
});

const selectedDate = computed(() => reservationStep1Form.selectedDate);
const { displayMonth } = useDisplayMonth(experienceId, selectedDate);

// 体験イベント(時間)選択
const { experienceEventDict, isLoading, candidateEvents, canReserveEventOptions } = useExperienceEventSelection({
  experience,
  experienceEvent,
  reservationStep1Form,
  experienceId,
  startDateFrom,
  startDateTo,
  onChangeExperienceEventId,
  initialUpdateFromExperienceEvent,
});
</script>
